import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { login as apiLogin, register as apiRegister } from '../services/api';
import api from '../services/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'USER' | 'ADMIN';
  emailVerified: boolean;
}

// Using simple JWT tokens instead of refresh tokens

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<User>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Using shared API service instead of custom instance

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setError(null);
      const response = await apiLogin({ email, password });
      
      if (!response.success) {
        // Handle specific error cases
        if (response.error === 'Email verification required') {
          throw new Error('Account found but email not verified. A new verification email has been sent to your email address. Please check your email and click the verification link to activate your account.');
        }
        const errorMessage = typeof response.error === 'string' ? response.error : (response.error as any)?.error?.message || 'Login failed';
        throw new Error(errorMessage);
      }
      
      const { user: userData, token } = response.data;
      
      setUser(userData);
      // Store just the token since that's what the backend provides
      localStorage.setItem('auth_token', token);
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      throw err;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string): Promise<User> => {
    try {
      setError(null);
      const response = await apiRegister({ email, password, firstName, lastName });
      
      if (!response.success) {
        const errorMessage = typeof response.error === 'string' ? response.error : (response.error as any)?.error?.message || 'Registration failed';
        const error = new Error(errorMessage);
        // Add status code if available for better error handling
        (error as any).status = (response as any).status || (response.error as any)?.status;
        // Preserve response for error handling in RegisterForm
        (error as any).response = { status: (error as any).status };
        throw error;
      }
      
      const { user: userData, token } = response.data;
      
      // Don't authenticate user until email is verified
      // Only store token for email verification process
      if (userData.emailVerified) {
        setUser(userData);
        localStorage.setItem('auth_token', token);
        setError(null); // Clear any previous errors
      } else {
        // User needs to verify email first
        setUser(null);
        localStorage.removeItem('auth_token');
        setError(null); // Clear any previous errors - we'll redirect to verification page instead
        // Return user data so the component can redirect to verification page
        return userData;
      }
      
      return userData;
    } catch (err: any) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      throw err;
    }
  };

  const logout = (): void => {
    setUser(null);
    setError(null);
    localStorage.removeItem('auth_token');
  };

  // Token refresh not implemented - using simple JWT tokens

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Clean up old token format
        const oldTokens = localStorage.getItem('auth_tokens');
        if (oldTokens) {
          console.log('Cleaning up old token format...');
          localStorage.removeItem('auth_tokens');
        }
        
        const storedToken = localStorage.getItem('auth_token');
        if (storedToken) {
          // Validate token by making a request to get user profile
          try {
            const response = await api.get('/auth/me');
            if (response.data && response.data.success && response.data.data) {
              const userData = response.data.data;
              // Only authenticate if email is verified (or user is admin)
              if (userData.emailVerified || userData.role === 'ADMIN') {
                setUser(userData);
                console.log('User authenticated from stored token');
              } else {
                // Email not verified, clear token and require verification
                localStorage.removeItem('auth_token');
                setUser(null);
                console.log('User email not verified, clearing token');
              }
            } else {
              // Invalid response, clear token
              localStorage.removeItem('auth_token');
              setUser(null);
            }
          } catch (error) {
            // Token is invalid, clear storage
            console.log('Token validation failed, clearing storage');
            localStorage.removeItem('auth_token');
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        // Token is invalid, clear storage
        console.error('Auth initialization error:', err);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_tokens'); // Also clean up old format
        setUser(null);
      } finally {
        // Always set loading to false after initialization
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    isLoading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
